import { useLayoutEffect, useRef, useState } from "react";
import { saveBlob, type DownloadedFile } from "@utils/fileDownload";

// A file as a list renders it: what to request, and the name to save it under
// when the server sends none.
export interface ListedFile {
    id: string;
    fileName: string;
}

export interface FileDownloadFailure {
    fileId: string;
    message: string;
}

interface DownloadState {
    scope: string;
    downloadingId: string | null;
    error: FileDownloadFailure | null;
}

// One list's file downloads on the admin client detail — a location's
// certificates, the organization's files: one download at a time, the
// server's name winning over the listed one, a failure kept under the file it
// belongs to.
// `scope` is what the files belong to (the location, the organization). When
// it changes — the page moves to another client — the list starts idle, and a
// download still on the wire for the previous scope is dropped when it lands:
// nothing saved, no state set. The same holds after unmount.
export const useFileDownload = (
    scope: string,
    download: (fileId: string) => Promise<DownloadedFile>,
    describeFailure: (error: unknown) => string,
) => {
    const [state, setState] = useState<DownloadState>({
        scope,
        downloadingId: null,
        error: null,
    });

    // A new scope starts idle in the same render, rather than one render
    // later from an effect: the previous scope's spinner must never show on
    // the next one's files.
    if (state.scope !== scope) {
        setState({ scope, downloadingId: null, error: null });
    }

    // Bumped by the cleanup, which runs on a scope change and on unmount —
    // synchronously in that commit, so no response can land in between. A
    // download applies its outcome only if the count is unchanged.
    const generation = useRef(0);
    useLayoutEffect(() => {
        const current = generation;

        return () => {
            current.current += 1;
        };
    }, [scope]);

    const start = async (file: ListedFile) => {
        const startedIn = generation.current;
        setState({ scope, downloadingId: file.id, error: null });

        let error: FileDownloadFailure | null = null;

        try {
            const downloaded = await download(file.id);

            if (generation.current !== startedIn) {
                return;
            }

            saveBlob(downloaded.blob, downloaded.fileName ?? file.fileName);
        } catch (caught) {
            if (generation.current !== startedIn) {
                return;
            }

            error = { fileId: file.id, message: describeFailure(caught) };
        }

        setState({ scope, downloadingId: null, error });
    };

    const current = state.scope === scope ? state : null;

    return {
        downloadingId: current?.downloadingId ?? null,
        error: current?.error ?? null,
        start,
    };
};
